import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { X, Plus, Check, Trash2, Package2, Info, AlertTriangle, Printer, Loader2 } from 'lucide-react';
import { PalletSummaryDto } from '../../../../types/picking';
import { printingService } from '../../../../services/printingService';
import { SSCCLabelType } from '../../../../types/printing';
import { useAuth } from '../../../../contexts/AuthContext';
import { ScanTextInput } from '../../../common/inputs';

interface PalletManagerProps {
  pallets: PalletSummaryDto[];
  activePalletSSCC: string | null;
  onCreatePallet: (ssccCode?: string) => void;
  onSetActivePallet: (sscc: string) => void;
  onDeletePallet: (sscc: string) => void;
  onClose: () => void;
}

const FIRST_TIME_INFO_KEY = 'palletManager_firstTimeInfoDismissed';

export const PalletManager: React.FC<PalletManagerProps> = ({
  pallets,
  activePalletSSCC,
  onCreatePallet,
  onSetActivePallet,
  onDeletePallet,
  onClose
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [newPalletSSCC, setNewPalletSSCC] = useState('');
  const [showFirstTimeInfo, setShowFirstTimeInfo] = useState(false);
  const [printingSSCC, setPrintingSSCC] = useState<string | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  const [printSuccess, setPrintSuccess] = useState<string | null>(null);

  // Handle SSCC label printing
  const handlePrintSSCC = async (pallet: PalletSummaryDto) => {
    if (!user?.companyNo) {
      setPrintError(t('warehouse.modules.picking.errors.missingCompanyInfo'));
      return;
    }

    setPrintingSSCC(pallet.sscc);
    setPrintError(null);
    setPrintSuccess(null);

    try {
      const result = await printingService.printSSCCLabel(user.companyNo, {
        sscc: pallet.sscc,
        quantity: pallet.totalQuantity || undefined,
        labelType: SSCCLabelType.StandardPallet,
        // Get first item's info if available
        gtin: pallet.items?.[0]?.gtin || undefined,
        batchNo: pallet.items?.[0]?.itemBatchNo || undefined,
        expiryDate: pallet.items?.[0]?.itemBatchExpDate || undefined
      });

      if (result.success) {
        setPrintSuccess(`SSCC-etikett skrevet ut for pall ${pallet.sscc}`);
        // Clear success message after 3 seconds
        setTimeout(() => setPrintSuccess(null), 3000);
      } else {
        setPrintError(result.message || 'Feil ved utskrift');
      }
    } catch (err: any) {
      console.error('Failed to print SSCC label:', err);
      setPrintError(err.message || 'Feil ved utskrift av SSCC-etikett');
    } finally {
      setPrintingSSCC(null);
    }
  };

  // Check if first-time info should be shown
  useEffect(() => {
    const dismissed = localStorage.getItem(FIRST_TIME_INFO_KEY);
    if (!dismissed && pallets.length === 0) {
      setShowFirstTimeInfo(true);
    }
  }, [pallets.length]);

  const handleCreatePallet = () => {
    onCreatePallet(newPalletSSCC);
    setNewPalletSSCC('');
  };

  const handleDismissFirstTimeInfo = () => {
    localStorage.setItem(FIRST_TIME_INFO_KEY, 'true');
    setShowFirstTimeInfo(false);
  };

  const getPalletStatusBadge = (pallet: PalletSummaryDto, isActive: boolean) => {
    const isEmpty = pallet.totalItems === 0;
    return (
      <div className="flex items-center gap-2">
        {isActive && (
          <span
            className="px-2 py-1 rounded text-xs font-bold text-white bg-[#F58220]"
            role="status"
            aria-label="Aktiv pall"
          >
            AKTIV
          </span>
        )}
        <span
          className={`px-2 py-1 rounded text-xs font-semibold ${
            isEmpty
              ? 'bg-gray-200 text-gray-700'
              : 'bg-[#E0F2F4] text-[#0E7C86]'
          }`}
          role="status"
          aria-label={isEmpty ? 'Tom pall' : 'Lagret pall'}
        >
          {isEmpty ? 'TOM' : 'LAGRET'}
        </span>
      </div>
    );
  };

  const handleDeletePallet = (pallet: PalletSummaryDto) => {
    const isEmpty = pallet.totalItems === 0;

    if (isEmpty) {
      // Delete empty pallet directly
      onDeletePallet(pallet.sscc);
    } else {
      // Require confirmation for filled pallets
      const confirmed = window.confirm(
        `Er du sikker på at du vil slette pall ${pallet.sscc}?\n\n` +
        `Pallen inneholder ${pallet.totalItems} varer (${pallet.totalQuantity} stk). ` +
        `Dette kan ikke angres.`
      );

      if (confirmed) {
        onDeletePallet(pallet.sscc);
      }
    }
  };

  const handleDeleteAllEmpty = () => {
    const confirmed = window.confirm(
      `Er du sikker på at du vil slette alle ${emptyPallets.length} tomme paller?`
    );

    if (confirmed) {
      emptyPallets.forEach(pallet => onDeletePallet(pallet.sscc));
    }
  };

  // Separate empty and filled pallets
  const emptyPallets = pallets.filter(p => p.totalItems === 0);
  const filledPallets = pallets.filter(p => p.totalItems > 0);

  return (
    <div className="fixed inset-0 sm:right-0 sm:left-auto sm:top-0 h-full w-full sm:w-96 bg-white shadow-xl sm:border-l border-gray-200 z-50 overflow-y-auto">
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold">{t('warehouse.modules.picking.pallets.management')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Lukk pallhåndtering"
          >
            <X size={24} />
          </button>
        </div>

        {/* Print success/error messages */}
        {printSuccess && (
          <div
            className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 flex items-center gap-2"
            role="status"
          >
            <Check size={18} className="flex-shrink-0" />
            <p className="text-sm">{printSuccess}</p>
          </div>
        )}

        {printError && (
          <div
            className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 flex items-center gap-2"
            role="alert"
          >
            <AlertTriangle size={18} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm">{printError}</p>
              <button
                onClick={() => setPrintError(null)}
                className="text-xs underline mt-1"
              >
                Lukk
              </button>
            </div>
          </div>
        )}

        {/* First-time info box */}
        {showFirstTimeInfo && (
          <div
            className="mb-4 p-4 rounded-lg border-2 border-[#0E7C86] bg-[#E0F2F4]"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start gap-3 mb-3">
              <Info size={20} className="text-[#0E7C86] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-[#0E7C86] mb-2">Hvordan paller fungerer</h4>
                <ul className="text-sm text-gray-700 space-y-1.5">
                  <li>• Tomme paller lagres bare midlertidig i minnet</li>
                  <li>• Når du fyller en pall med varer, lagres den permanent</li>
                  <li>• Tomme paller kan slettes uten bekreftelse</li>
                  <li>• Fylte paller krever bekreftelse for sletting</li>
                </ul>
              </div>
            </div>
            <button
              onClick={handleDismissFirstTimeInfo}
              className="w-full text-sm font-medium text-[#0E7C86] hover:text-[#0A5F66] transition-colors min-h-[44px]"
            >
              Forstått, ikke vis igjen
            </button>
          </div>
        )}

        {/* Warning for many empty pallets */}
        {emptyPallets.length > 3 && (
          <div
            className="mb-4 p-3 rounded-lg border-2 border-amber-400 bg-amber-50"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                Du har <strong>{emptyPallets.length} tomme paller</strong>. Disse lagres ikke permanent før de fylles med varer.
              </p>
            </div>
            <button
              onClick={handleDeleteAllEmpty}
              className="w-full mt-2 text-sm font-medium text-amber-700 hover:text-amber-900 py-2 px-3 rounded border border-amber-400 hover:bg-amber-100 transition-colors min-h-[44px]"
              aria-label={`Slett alle ${emptyPallets.length} tomme paller`}
            >
              <Trash2 size={14} className="inline mr-1.5" />
              Slett alle tomme paller
            </button>
          </div>
        )}

        <div className="mb-4 sm:mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium mb-3">Opprett ny pall</h4>
          <ScanTextInput
            placeholder="SSCC-kode (valgfritt)"
            className="w-full p-3 sm:p-2 border border-gray-300 rounded mb-3 min-h-[44px]"
            value={newPalletSSCC}
            onChange={(e) => setNewPalletSSCC(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreatePallet()}
            aria-label="SSCC-kode for ny pall"
          />
          <button
            onClick={handleCreatePallet}
            className="w-full bg-[#0E7C86] text-white py-3 sm:py-2 rounded hover:bg-[#0A5F66] transition-colors font-medium min-h-[44px]"
            aria-label="Opprett ny pall"
          >
            <Plus size={16} className="inline mr-2" />
            Opprett pall
          </button>
        </div>

        <div className="space-y-4">
          {pallets.map((pallet) => {
            const isActive = pallet.sscc === activePalletSSCC;
            const isEmpty = pallet.totalItems === 0;

            return (
              <div
                key={pallet.sscc}
                role="article"
                aria-label={`Pall ${pallet.sscc}, ${isEmpty ? 'tom' : `${pallet.totalItems} varer`}${isActive ? ', aktiv' : ''}`}
                className={`rounded-lg p-4 transition-all ${
                  isEmpty
                    ? 'bg-gray-50 border-2 border-dashed border-gray-300'
                    : isActive
                    ? 'bg-white border-2 border-solid border-[#F58220] shadow-lg shadow-orange-100'
                    : 'bg-white border-2 border-solid border-[#0E7C86]'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="font-semibold text-gray-900">Pall {pallet.sscc}</h5>
                      {isEmpty && (
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label="Informasjon om tom pall"
                          title="Lagres ikke før den fylles med varer"
                        >
                          <Info size={14} />
                        </button>
                      )}
                    </div>
                    {getPalletStatusBadge(pallet, isActive)}
                    <p className="text-xs text-gray-500 mt-1">{pallet.sscc}</p>
                    {pallet.ssccParent && (
                      <p className="text-xs text-gray-500">Parent: {pallet.ssccParent}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {pallet.sscc !== activePalletSSCC && (
                      <button
                        onClick={() => onSetActivePallet(pallet.sscc)}
                        className="text-[#0E7C86] hover:text-[#0A5F66] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label={`Aktiver pall ${pallet.sscc}`}
                        title="Aktiver pall"
                      >
                        <Check size={20} />
                      </button>
                    )}
                    <button
                      onClick={() => handlePrintSSCC(pallet)}
                      disabled={printingSSCC === pallet.sscc}
                      className="text-blue-600 hover:text-blue-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
                      aria-label={`Skriv ut SSCC-etikett for pall ${pallet.sscc}`}
                      title="Skriv ut SSCC-etikett"
                    >
                      {printingSSCC === pallet.sscc ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        <Printer size={20} />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeletePallet(pallet)}
                      className="text-red-600 hover:text-red-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                      aria-label={`Slett pall ${pallet.sscc}`}
                      title={isEmpty ? 'Slett tom pall' : 'Slett pall (krever bekreftelse)'}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                {!isEmpty && (
                  <>
                    <div className="text-sm text-gray-700 mb-3 font-medium">
                      {pallet.totalItems} {pallet.totalItems === 1 ? 'vare' : 'varer'}, {pallet.totalQuantity} stk, {pallet.totalWeight}kg
                    </div>

                    <div className="space-y-1">
                      {pallet.items.map((item) => (
                        <div key={`${item.orderNo}-${item.lineNo}`} className="text-xs text-gray-600 py-1 px-2 bg-gray-50 rounded">
                          Ordre {item.orderNo}, Linje {item.lineNo}: <strong>{item.quantity} stk</strong>
                          {item.gtin && <span className="text-gray-500"> (GTIN: {item.gtin})</span>}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {isEmpty && (
                  <p className="text-sm text-gray-500 italic mt-2">
                    Pallen er tom og lagres ikke permanent før den fylles med varer.
                  </p>
                )}
              </div>
            );
          })}

          {pallets.length === 0 && (
            <div className="text-center text-gray-500 py-12">
              <Package2 size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-gray-600">Ingen paller opprettet ennå</p>
              <p className="text-sm mt-1">Opprett en pall for å starte plukkingen</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
